import React, {useMemo, useContext, useState} from 'react';
import {
  getAiApiBaseUrl,
  getDefaultAiApiBaseUrl,
  setAiApiBaseUrl,
} from '../helpers/aiApiConfig';
import {getAppApiLabel, requiresAppApiAuth} from '../helpers/appApiConfig';
import {useAiAvailable} from '../contexts/AiAvailability';
import {Context} from '../contexts/WordList';
import {useTheme} from '../contexts/Theme';
import {BulkEnrichPanel} from '../components/BulkEnrichPanel';

function normalizeUrl(value) {
  return value.trim().replace(/\/+$/, '');
}

function isValidHttpUrl(value) {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

export const AdminPage = () => {
  const [wordListState, wordListDispatch] = useContext(Context);
  const {language} = wordListState;
  const {theme, toggleTheme} = useTheme();
  const defaultHost = useMemo(() => getDefaultAiApiBaseUrl(), []);
  const appApiBase = useMemo(() => getAppApiLabel(), []);
  const appAuthMode = useMemo(
    () => (requiresAppApiAuth() ? 'Sign-in required' : 'Local development bypass'),
    [],
  );
  const presets = useMemo(() => {
    const browserHost =
      typeof window !== 'undefined' ? window.location.hostname : '';
    const lanHost =
      browserHost && browserHost !== 'localhost' && browserHost !== '127.0.0.1'
        ? `http://${browserHost}:5175`
        : 'http://192.168.1.20:5175';

    return [
      {id: 'local', label: 'Local', value: 'http://localhost:5175'},
      {id: 'lan', label: 'LAN', value: lanHost},
      {id: 'cloud', label: 'Cloud', value: 'https://api.example.com'},
    ];
  }, []);
  const [hostInput, setHostInput] = useState(getAiApiBaseUrl());
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const {aiAvailable, aiChecking, refreshAiAvailability} = useAiAvailable();
  const activeHost = getAiApiBaseUrl();
  const activePresetId = useMemo(() => {
    const normalizedInput = normalizeUrl(hostInput);
    const matched = presets.find(
      (preset) => normalizeUrl(preset.value) === normalizedInput,
    );
    return matched ? matched.id : '';
  }, [hostInput, presets]);

  const handleSave = async (e) => {
    e.preventDefault();

    const trimmed = hostInput.trim();
    if (!trimmed) {
      setMessageType('error');
      setMessage('Host URL cannot be empty.');
      return;
    }

    if (!isValidHttpUrl(trimmed)) {
      setMessageType('error');
      setMessage('Please enter a valid http:// or https:// URL.');
      return;
    }

    const savedHost = setAiApiBaseUrl(trimmed);
    setHostInput(savedHost);
    setMessageType('success');
    setMessage('AI API host saved. Checking service availability...');
    await refreshAiAvailability();
  };

  const handleResetDefault = async () => {
    const restoredHost = setAiApiBaseUrl(defaultHost);
    setHostInput(restoredHost);
    setMessageType('success');
    setMessage('AI API host reset to default. Checking service availability...');
    await refreshAiAvailability();
  };

  const handleRecheck = async () => {
    setMessageType('success');
    setMessage('Checking AI service availability...');
    await refreshAiAvailability();
  };

  const handlePresetSelect = (presetValue) => {
    setHostInput(presetValue);
    setMessage('');
  };

  const handleLanguageChange = (e) => {
    wordListDispatch({type: 'CHANGE_LANGUAGE', payload: e.target.value});
  };

  return (
    <div className="page-container admin-page-wrap">
      <section className="admin-panel">
        <h2 className="admin-title">Admin Settings</h2>
        <p className="admin-subtitle">
          Configure the base host URL used for AI requests.
        </p>

        <div className="admin-diagnostics-card">
          <div className="admin-diagnostics-row">
            <span className="admin-diagnostics-key">Language</span>
            <span className="admin-diagnostics-value">
              <select
                className="field"
                value={language}
                onChange={handleLanguageChange}
                aria-label="Language"
              >
                <option value="farsi">Farsi</option>
                <option value="hebrew">Hebrew</option>
              </select>
            </span>
          </div>
          <div className="admin-diagnostics-row">
            <span className="admin-diagnostics-key">Theme</span>
            <span className="admin-diagnostics-value">
              <button
                type="button"
                className={`theme-toggle-btn ${theme}`}
                onClick={toggleTheme}
                aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
              >
                <span className="theme-toggle-track">
                  <span className="theme-toggle-thumb" />
                  <span className="theme-toggle-icons">
                    <span className="theme-toggle-icon-light">&#9788;</span>
                    <span className="theme-toggle-icon-dark">&#9790;</span>
                  </span>
                </span>
                <span className="theme-toggle-label">{theme === 'light' ? 'Light' : 'Dark'}</span>
              </button>
            </span>
          </div>
          <div className="admin-diagnostics-row">
            <span className="admin-diagnostics-key">App API</span>
            <span className="admin-diagnostics-value">{appApiBase}</span>
          </div>
          <div className="admin-diagnostics-row">
            <span className="admin-diagnostics-key">App Access</span>
            <span className="admin-diagnostics-value">{appAuthMode}</span>
          </div>
        </div>

        <form
          id="admin-host-form"
          className="admin-form"
          onSubmit={handleSave}
        >
          <label className="admin-label">Environment Preset</label>
          <div className="admin-segment" role="tablist" aria-label="Host presets">
            {presets.map((preset) => (
              <button
                key={preset.id}
                type="button"
                role="tab"
                className={`admin-segment-btn${activePresetId === preset.id ? ' active' : ''}`}
                aria-selected={activePresetId === preset.id}
                onClick={() => handlePresetSelect(preset.value)}
              >
                {preset.label}
              </button>
            ))}
          </div>

          <label htmlFor="ai-api-host" className="admin-label">
            AI API Host URL
          </label>
          <input
            id="ai-api-host"
            className="field admin-input"
            value={hostInput}
            onChange={(e) => setHostInput(e.target.value)}
            placeholder="http://localhost:5175"
            autoComplete="off"
          />

          <div className="admin-default-row">
            <span>Default:</span>
            <span className="admin-default-value">{defaultHost}</span>
          </div>

          <div className="admin-actions">
            <button
              type="button"
              className="btn"
              onClick={handleResetDefault}
            >
              Reset To Default
            </button>
          </div>
        </form>

        <div className="admin-status-row">
          <span className={`admin-status-dot ${aiAvailable ? 'online' : 'offline'}`} />
          <span>
            {aiChecking ? 'Checking AI service...' : aiAvailable ? 'AI service is reachable' : 'AI service is offline'}
          </span>
        </div>

        <BulkEnrichPanel />

        <div className="admin-diagnostics-card">
          <div className="admin-diagnostics-row">
            <span className="admin-diagnostics-key">Active Host</span>
            <span className="admin-diagnostics-value">{activeHost}</span>
          </div>
          <div className="admin-diagnostics-row">
            <span className="admin-diagnostics-key">Default Host</span>
            <span className="admin-diagnostics-value">{defaultHost}</span>
          </div>
          <div className="admin-diagnostics-row">
            <span className="admin-diagnostics-key">Status</span>
            <span className={`admin-pill ${aiAvailable ? 'online' : 'offline'}`}>
              {aiChecking ? 'Checking' : aiAvailable ? 'Online' : 'Offline'}
            </span>
          </div>
        </div>

        <div className="admin-help-card">
          <h3 className="admin-help-title">iPhone Setup Tips</h3>
          <p className="admin-help-item">Use your computer LAN IP (for example http://192.168.1.20:5175) instead of localhost when testing on iPhone.</p>
          <p className="admin-help-item">Keep the AI server on the same Wi-Fi network as your phone and ensure port 5175 is reachable.</p>
          <p className="admin-help-item">After changing the host, tap Re-check Service to confirm live connectivity.</p>
          <p className="admin-help-item">When the app API points at production, this app now requires sign-in even during local development.</p>
        </div>

        {message && (
          <div className={`admin-message ${messageType === 'error' ? 'error' : 'success'}`}>
            {message}
          </div>
        )}
      </section>

      <div className="admin-sticky-actions" aria-label="Admin quick actions">
        <button type="submit" form="admin-host-form" className="btn btn-primary admin-sticky-primary">
          Save Host
        </button>
        <button type="button" className="btn admin-sticky-secondary" onClick={handleRecheck}>
          Re-check Service
        </button>
      </div>
    </div>
  );
};
